import {
  createRootRoute,
  createRoute,
  createRouter,
  Navigate,
  useLocation,
} from "@tanstack/react-router";
import { useAuth } from "../lib/auth";
import { AppLayout } from "./app-layout";
import { parseIncidentListSearch } from "../features/incidents/search";
import { DashboardPage } from "../features/dashboard/dashboard-page";
import { TrendsPage } from "../features/dashboard/trends-page";
import { IncidentListPage } from "../features/incidents/incident-list-page";
import { IncidentCreatePage } from "../features/incidents/incident-create-page";
import { IncidentDetailPage } from "../features/incidents/incident-detail-page";
import { PostmortemPage } from "../features/postmortem/postmortem-page";
import { DsgvoPage } from "../features/dsgvo/dsgvo-page";
import { AdminUsersPage } from "../features/admin-users/admin-users-page";
import { AdminMasterDataPage } from "../features/admin-master-data/admin-master-data-page";
import { AccessStatusPage } from "../features/admin-access/status-page";
import { AccessJournalPage } from "../features/admin-access/journal-page";
import { AccountPage } from "../features/account/account-page";
import { PublicLayout } from "../features/access/public-layout";
import { LoginPage } from "../features/access/login-page";
import { FirstLoginPage } from "../features/access/first-login-page";
import { ResetPage } from "../features/access/reset-page";
import { PUBLIC_PATHS, landingFor, parseLoginSearch } from "../features/access/routing";
import { OperationsLogPage } from "../features/operations-log/operations-log-page";
import { OperationsLogEntryPage } from "../features/operations-log/operations-log-entry-page";

/**
 * Code-based TanStack Router (no route-generation plugin). The root picks the
 * frame by path:
 *
 *   public - the pages without a session ([F-029]): /anmelden,
 *            /erste-anmeldung (the invitation link's target - its path is in
 *            every invitation mail, keep it), /passwort-vergessen.
 *   app    - everything else, behind the gate in app-layout.tsx.
 *
 * A frame chosen by path rather than by a pathless layout route keeps every
 * route id equal to its path, which the screens' `useParams({ from })` rely on.
 *
 * The public paths are German because the person reads them - in the address
 * bar and in the invitation mail; the application's existing paths stay as
 * they are.
 */
function RootLayout(): JSX.Element {
  const { pathname } = useLocation();
  return PUBLIC_PATHS.includes(pathname) ? <PublicLayout /> : <AppLayout />;
}

const rootRoute = createRootRoute({ component: RootLayout });

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/anmelden",
  validateSearch: parseLoginSearch,
  component: LoginPage,
});

const firstLoginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/erste-anmeldung",
  component: FirstLoginPage,
});

const resetRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/passwort-vergessen",
  component: ResetPage,
});

/** F-020 MUSS: the dashboard is the first screen after login; [F-031] the system account's is the administration. */
function IndexRedirect(): JSX.Element {
  const { isSystem } = useAuth();
  return <Navigate to={landingFor(isSystem)} replace />;
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: IndexRedirect,
});

const dashboardRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dashboard",
  component: DashboardPage,
});

// F-023: the Trend-Dashboard, a view of its own for the manager role.
const trendsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/trends",
  component: TrendsPage,
});

const incidentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/incidents",
  // Validated deep-link filters for the dashboard drilldowns (F-020/AC-020).
  validateSearch: parseIncidentListSearch,
  component: IncidentListPage,
});

const newIncidentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/incidents/new",
  component: IncidentCreatePage,
});

const incidentDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/incidents/$id",
  component: IncidentDetailPage,
});

const postmortemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/incidents/$id/postmortem",
  component: PostmortemPage,
});

// Betriebslog (Hauptansicht 5): the chronological list and the single entry.
const operationsLogRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations-log",
  component: OperationsLogPage,
});

const operationsLogEntryRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/operations-log/$id",
  component: OperationsLogEntryPage,
});

const dsgvoRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/dsgvo",
  component: DsgvoPage,
});

const accountRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/konto",
  component: AccountPage,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/users",
  component: AdminUsersPage,
});

const adminStatusRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/status",
  component: AccessStatusPage,
});

const adminJournalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/journal",
  component: AccessJournalPage,
});

const adminMasterDataRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin/master-data",
  component: AdminMasterDataPage,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  firstLoginRoute,
  resetRoute,
  indexRoute,
  dashboardRoute,
  trendsRoute,
  incidentsRoute,
  newIncidentRoute,
  incidentDetailRoute,
  postmortemRoute,
  operationsLogRoute,
  operationsLogEntryRoute,
  dsgvoRoute,
  accountRoute,
  adminUsersRoute,
  adminStatusRoute,
  adminJournalRoute,
  adminMasterDataRoute,
]);

export const router = createRouter({
  routeTree,
  defaultPreload: "intent",
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
